import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import ContactForm from "../components/ContactForm";
import VoiceVisualizer from "../components/VoiceVisualizer";
import Seo from "../components/Seo";
import { Reveal, FrequencyWave } from "../components/motion";

const defaultSeo = {
  "soul-in-love": {
    title: "Soul in Love | Sound & Voice Experiences for Deeper Listening",
    description:
      "Soul in Love is the contemplative expression of The SEAL Collective — channeled work, vocal frequencies, sound experiences, teachings, and meditations rooted in love and inner knowing.",
  },
  "amber-ellis": {
    title: "Amber Ellis | Recording Artist, Vocalist & Voice Talent",
    description:
      "Recording artist and professional voice talent Amber Ellis brings decades of performance and communication experience into music, narration, and immersive vocal work.",
  },
  tundala: {
    title: "Tundala | A Sound & Voice Companion — Chants, Tones & Frequencies",
    description:
      "Tundala is an emerging sound-based companion from The SEAL Collective — chants, tones, guided sound, and vocal experiences born from the Soul in Love sound library.",
  },
};

const ExpressionLayout = ({ data }) => {
  const [interest, setInterest] = useState(data.interest);
  const enquire = (topic) => {
    setInterest(topic);
    setTimeout(
      () => document.getElementById("expression-contact")?.scrollIntoView({ behavior: "smooth" }),
      50
    );
  };
  const seo = {
    title: data.seoTitle || defaultSeo[data.slug]?.title,
    description: data.seoDescription || defaultSeo[data.slug]?.description,
  };

  return (
  <main data-testid={`expression-page-${data.slug}`}>
    <Seo title={seo.title} description={seo.description} path={`/${data.slug}`} />
    <section className="relative overflow-hidden px-6 pt-40 pb-24 lg:pt-52 lg:pb-32">
      <div className="pointer-events-none absolute inset-0">
        <div className="glow-pulse absolute left-1/2 top-24 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.14)_0%,transparent_62%)]" />
        <FrequencyWave tone="dark" className="absolute bottom-6 left-0 h-32 w-full" opacity={0.5} />
      </div>
      <div className="relative z-10 mx-auto max-w-4xl">
        <Reveal>
          <Link
            data-testid={`${data.slug}-back-link`}
            to="/"
            className="mb-12 inline-flex items-center gap-2 text-[0.6rem] font-light uppercase tracking-[0.3em] text-black/45 transition-colors duration-500 hover:text-[#8a6d1f]"
          >
            <ArrowLeft size={12} strokeWidth={1.5} /> The Collective
          </Link>
          <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#8a6d1f]">
            {data.kicker}
          </p>
          <h1
            data-testid={`${data.slug}-headline`}
            className="font-serif-display text-5xl font-light leading-[1.05] tracking-tight text-[#16130e] sm:text-6xl"
          >
            {data.title}
          </h1>
          <p
            data-testid={`${data.slug}-tagline`}
            className="font-serif-display mt-6 text-xl font-light italic text-[#8a6d1f] md:text-2xl"
          >
            {data.tagline}
          </p>
          <p className="mt-8 max-w-2xl text-base font-extralight leading-relaxed text-black/60 md:text-lg">
            {data.lead}
          </p>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-4xl px-6 pb-24">
      <div className="space-y-20">
        {data.chapters.map((c, i) => (
          <Reveal key={c.heading} delay={i * 0.06}>
            <article data-testid={`${data.slug}-chapter-${i + 1}`}>
              <span className="mb-6 block h-px w-16 bg-gradient-to-r from-[#b8912e]/60 to-transparent" />
              <h2 className="font-serif-display text-2xl font-light text-[#16130e] md:text-3xl">{c.heading}</h2>
              <p className="mt-5 max-w-2xl text-sm font-extralight leading-loose text-black/60 md:text-base">
                {c.body}
              </p>
            </article>
          </Reveal>
        ))}
      </div>

      {data.programs && (
        <div className="mt-24">
          <Reveal>
            <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#8a6d1f]">
              {data.programsTitle}
            </p>
            <p className="max-w-2xl text-sm font-extralight leading-relaxed text-black/60 md:text-base">
              {data.programsIntro}
            </p>
          </Reveal>
          <div data-testid="org-programs" className="mt-12 space-y-px bg-black/10">
            {data.programs.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.06}>
                <article
                  data-testid={`org-program-${i + 1}`}
                  className="flex flex-col gap-4 bg-[#faf8f4] p-8 md:flex-row md:items-center md:justify-between md:p-10"
                >
                  <div className="max-w-2xl">
                    <p className="text-[0.6rem] font-light uppercase tracking-[0.3em] text-[#8a6d1f]">{p.format}</p>
                    <h3 className="font-serif-display mt-2 text-2xl font-light text-[#16130e]">{p.name}</h3>
                    <p className="mt-3 text-sm font-extralight leading-relaxed text-black/55">{p.text}</p>
                  </div>
                  <button
                    data-testid={`org-program-${i + 1}-enquire-button`}
                    onClick={() => enquire("Sales from the Heart")}
                    className="btn-outline w-fit shrink-0 !text-[0.6rem]"
                  >
                    Enquire
                  </button>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      {data.pillars && (
        <div className="mt-24">
          <Reveal>
            <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#8a6d1f]">
              {data.pillarsTitle}
            </p>
          </Reveal>
          <div data-testid="org-pillars" className="grid grid-cols-1 gap-px bg-black/10 sm:grid-cols-2 lg:grid-cols-3">
            {data.pillars.map((p, i) => (
              <div key={p.name} className="bg-[#faf8f4] p-8">
                <Reveal delay={i * 0.05}>
                  <h3 className="font-serif-display text-xl font-light text-[#16130e]">{p.name}</h3>
                  <p className="mt-3 text-sm font-extralight leading-relaxed text-black/55">{p.text}</p>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.science && (
        <div className="theme-dark relative mt-24 overflow-hidden bg-[#0a0908] px-8 py-16 md:px-14" data-testid="org-science">
          <div className="glow-pulse pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.12)_0%,transparent_65%)]" />
          <Reveal>
            <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#d4af37]">
              {data.scienceTitle}
            </p>
            <ul className="space-y-6">
              {data.science.map((s) => (
                <li
                  key={s}
                  className="font-serif-display flex items-baseline gap-4 text-lg font-light italic leading-relaxed text-white/80 md:text-xl"
                >
                  <span className="h-1 w-1 shrink-0 translate-y-[-4px] rounded-full bg-[#d4af37]" />
                  {s}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      )}

      <Reveal delay={0.1}>
        <p
          data-testid={`${data.slug}-keyline`}
          className="font-serif-display mt-24 border-l border-[#b8912e]/50 pl-8 text-2xl font-light leading-snug text-gold-gradient md:text-4xl"
        >
          {data.keyline}
        </p>
      </Reveal>
    </section>

      {data.externalLinks && (
        <Reveal delay={0.12}>
          <div data-testid={`${data.slug}-external-links`} className="mx-auto mt-24 max-w-4xl border-t hairline px-6 pt-16">
            <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#8a6d1f]">
              {data.externalKicker}
            </p>
            <h3 className="font-serif-display text-2xl font-light text-[#16130e] md:text-3xl">
              {data.externalHeading}
            </h3>
            <p className="mt-4 max-w-2xl text-sm font-extralight leading-relaxed text-black/60">
              {data.externalIntro}
            </p>
            <div className={`mt-10 grid grid-cols-1 gap-px bg-black/10 sm:grid-cols-2 ${data.externalLinks.length >= 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
              {data.externalLinks.map((l) => (
                <a
                  key={l.label}
                  data-testid={`${data.slug}-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between bg-[#faf8f4] p-6 transition-colors duration-500 hover:bg-[#b8912e]/5"
                >
                  <div>
                    <p className="font-serif-display text-lg font-light text-[#16130e]">{l.label}</p>
                    <p className="mt-1 text-xs font-extralight text-black/45">{l.sub}</p>
                  </div>
                  <ArrowUpRight
                    size={15}
                    strokeWidth={1.5}
                    className="text-black/30 transition-colors duration-500 group-hover:text-[#8a6d1f]"
                  />
                </a>
              ))}
            </div>
          </div>
        </Reveal>
      )}

    {data.special === "visualizer" && (
      <section className="theme-dark relative overflow-hidden bg-[#0a0908] py-32" data-testid="tundala-visualizer-section">
        <FrequencyWave tone="light" className="absolute top-8 left-0 h-24 w-full" opacity={0.35} />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#d4af37]">
              A First Glimpse
            </p>
            <h2 className="font-serif-display text-3xl font-light text-white sm:text-4xl">
              HEAR YOURSELF <span className="text-gold-gradient-light">DIFFERENTLY.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-sm font-extralight leading-relaxed text-white/55 md:text-base">
              Speak, hum, or sing — and watch your voice become living geometry. This is the seed of Tundala.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-14">
            <VoiceVisualizer />
          </Reveal>
        </div>
      </section>
    )}

    {data.special === "orgapp" && (
      <section className="theme-dark relative overflow-hidden bg-[#0a0908] py-32" data-testid="org-app-section">
        <div className="glow-pulse pointer-events-none absolute right-0 top-0 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.1)_0%,transparent_62%)]" />
        <div className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2">
          <Reveal>
            <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#d4af37]">
              Currently in Build
            </p>
            <h2 className="font-serif-display text-3xl font-light leading-tight text-white sm:text-4xl">
              THE ORGANIZATION <span className="text-gold-gradient-light">APP</span>
            </h2>
            <p className="mt-6 max-w-lg text-sm font-extralight leading-relaxed text-white/55 md:text-base">
              A home is being prepared for the SEAL organizational experience — a human-centered platform for
              teams to practice communication, mindfulness, and culture in the rhythm of real work.
            </p>
            <p className="mt-4 max-w-lg text-sm font-extralight leading-relaxed text-white/55 md:text-base">
              This platform is actively being built and is not yet available. Organizations joining the
              early-access list will be the first invited in when it opens.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Guided team practices and voice-led resets",
                "Culture pulse and human-centered check-ins",
                "Leadership pathways grounded in presence",
              ].map((f) => (
                <li key={f} className="flex items-baseline gap-3 text-sm font-extralight text-white/65">
                  <span className="h-1 w-1 shrink-0 translate-y-[-3px] rounded-full bg-[#d4af37]" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              data-testid="org-app-early-access-button"
              onClick={() => enquire("Organizations")}
              className="btn-outline mt-10"
            >
              Request Early Access
            </button>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative border hairline-light bg-white/[0.03] p-8 backdrop-blur-sm">
              <div className="mb-6 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-white/15" />
                <span className="h-2 w-2 rounded-full bg-white/15" />
                <span className="h-2 w-2 rounded-full bg-[#d4af37]/60" />
              </div>
              <div className="space-y-4">
                <div className="h-3 w-1/3 bg-white/10" />
                <div className="h-3 w-2/3 bg-white/[0.06]" />
                <div className="h-3 w-1/2 bg-white/[0.06]" />
                <div className="mt-8 grid grid-cols-3 gap-3">
                  <div className="h-20 border hairline-light bg-white/[0.04]" />
                  <div className="h-20 border border-[#d4af37]/40 bg-[#d4af37]/10" />
                  <div className="h-20 border hairline-light bg-white/[0.04]" />
                </div>
                <div className="h-3 w-1/2 bg-white/[0.06]" />
                <div className="h-3 w-2/5 bg-white/[0.06]" />
              </div>
              <p className="mt-8 text-center text-[0.6rem] font-light uppercase tracking-[0.35em] text-white/35">
                Interface currently in build
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    )}

    <section id="expression-contact" className="scroll-mt-24 px-6 py-32">
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#8a6d1f]">
            {data.ctaKicker}
          </p>
          <h2 className="font-serif-display text-3xl font-light text-[#16130e] sm:text-4xl">
            {data.ctaHeading}
          </h2>
        </Reveal>
        <div className="mt-14">
          <ContactForm key={interest} defaultInterest={interest} />
        </div>
      </div>
    </section>
    <footer data-testid={`${data.slug}-footer`} className="theme-dark border-t hairline-light bg-[#0a0908]">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-6 py-10 lg:px-12">
        <span className="text-[0.6rem] font-light uppercase tracking-[0.35em] text-white/30">
          Follow the work
        </span>
        {[
          { label: "Instagram", url: "https://www.instagram.com/soul_inlove_/", testId: "footer-social-instagram" },
          { label: "YouTube", url: "https://www.youtube.com/@SoulinloveChannelings", testId: "footer-social-youtube" },
          { label: "TikTok", url: "https://www.tiktok.com/@soulinlove_", testId: "footer-social-tiktok" },
          { label: "Facebook", url: "https://www.facebook.com/profile.php?id=61587527752249", testId: "footer-social-facebook" },
          { label: "soulinlove.com", url: "https://soulinlove.com", testId: "footer-social-soulinlove" },
        ].map((s) => (
          <a
            key={s.testId}
            data-testid={s.testId}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[0.6rem] font-light uppercase tracking-[0.3em] text-white/50 transition-colors duration-500 hover:text-[#f3e5ab]"
          >
            {s.label}
          </a>
        ))}
      </div>
      <div className="border-t hairline-light py-6 text-center text-[0.6rem] font-extralight tracking-[0.3em] text-white/25">
        © {new Date().getFullYear()} THE SEAL COLLECTIVE
      </div>
    </footer>
  </main>
  );
};

export default ExpressionLayout;
